import React, { useMemo } from 'react';

import { cssMap, cx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { AgentAvatar } from '@atlaskit/rovo-agent-components';
import { getAgentAvatarColor } from '@atlaskit/rovo-agent-components/ui/agent-avatar/GeneratedAvatar';
import { token } from '@atlaskit/tokens';

import { isForgeAgent } from '@atlassian/conversation-assistant-agent';

import { AgentRowAvatar } from '@confluence/agent-user-dual-avatar/entry-points/AgentRowAvatar.ts';
import type {
	PresenceHexOutline,
	RovoPresenceColorMode,
} from '@confluence/agent-user-dual-avatar/entry-points/AgentRowAvatar.ts';
import { useAgentAvatarColorMode } from '@confluence/agent-user-dual-avatar/entry-points/useAgentAvatarColorMode.ts';
import { fg } from '@confluence/feature-gating';

import {
	AGENT_AVATAR_SHELL_PATH,
	AGENT_AVATAR_SHELL_SIZE,
	AgentPresenceOutline,
} from '../shared/AgentPresenceOutline.tsx';
import type { AgentAvatarSize } from '../shared/AgentPresenceOutline.tsx';
import type { AgentWithPresence } from './AgentPresenceTypes.ts';

type PresenceArtworkOptions = {
	borderColor: string;
	colorMode: RovoPresenceColorMode;
	presenceTileFill?: string;
	presenceOutline?: PresenceHexOutline;
};

const AGENT_AVATAR_SHELL_BORDER_WIDTH = 1;
const styles = cssMap({
	root: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
		position: 'relative',
	},
	small: {
		height: '27px',
		width: '24px',
	},
	medium: {
		height: '35px',
		width: '32px',
	},
	overlapped: {
		marginInlineStart: token('space.negative.075'),
	},
	avatarContent: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		position: 'relative',
	},
	shell: {
		display: 'block',
		position: 'absolute',
		left: '50%',
		pointerEvents: 'none',
	},
	legacyBorder: {
		position: 'relative',
		filter: `drop-shadow(1px 0px 0px ${token('color.icon.inverse')}) drop-shadow(-1px 0px 0px ${token('color.icon.inverse')}) drop-shadow(0px 1px 0px ${token('color.icon.inverse')}) drop-shadow(0px -1px 0px ${token('color.icon.inverse')})`,
	},
});

const AgentAvatarShell = ({
	backgroundColor,
	borderColor,
	size,
}: {
	backgroundColor: string;
	borderColor: string;
	size: AgentAvatarSize;
}): React.JSX.Element => {
	const { width, height } = AGENT_AVATAR_SHELL_SIZE[size];

	return (
		<Box
			as="span"
			xcss={styles.shell}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Exposes the exact fractional shell offset, rotation, and opacity required by the design specification.
			style={{ top: '0.2px', transform: 'translateX(-50%) rotate(0deg)', opacity: 1 }}
			testId="agent-presence-avatar-shell"
		>
			<svg
				data-testid="agent-presence-avatar-shell-svg"
				display="block"
				width={width}
				height={height}
				viewBox={`0 0 ${width} ${height}`}
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
				aria-hidden="true"
				focusable="false"
			>
				<path
					data-testid="agent-presence-avatar-shell-shape"
					d={AGENT_AVATAR_SHELL_PATH[size]}
					fill={backgroundColor}
					stroke={borderColor}
					strokeWidth={AGENT_AVATAR_SHELL_BORDER_WIDTH}
					vectorEffect="non-scaling-stroke"
				/>
			</svg>
		</Box>
	);
};

const getShellColors = (
	agent: AgentWithPresence,
	borderColor?: string,
): { backgroundColor: string; borderColor: string } => {
	const { primary } = getAgentAvatarColor({
		agentIdentityAccountId: agent.identity_account_id,
		agentId: agent.id,
		agentNamedId: agent.external_config_reference ?? agent.named_id,
	});

	return { backgroundColor: primary, borderColor: borderColor ?? primary };
};

const isNormallyWhiteBrand = (agentIcon: AgentWithPresence['agentIcon']): boolean => {
	switch (agentIcon) {
		case 'antigravity':
		case 'codex':
		case 'gemini':
		case 'hermes':
		case 'microsoft-copilot':
		case 'vscode':
			return true;
		default:
			return false;
	}
};

export type AgentPresenceAvatarProps = {
	agent: AgentWithPresence;
	isOverlapped?: boolean;
	stackIndex?: number;
	showBorder?: boolean;
	size?: AgentAvatarSize;
};

export function AgentPresenceAvatar({
	agent,
	isOverlapped,
	stackIndex,
	showBorder,
	size = 'medium',
}: AgentPresenceAvatarProps): React.JSX.Element {
	const colorMode = useAgentAvatarColorMode();
	let isAgentPresenceMoveEnabled = false;
	if (fg('platform_move_presence_agents')) {
		isAgentPresenceMoveEnabled = true;
	}
	const isRefinedGeometryEnabled =
		isAgentPresenceMoveEnabled && fg('confluence_refined_agent_presence_geometries');
	const usesSharedFacepileOutline = isRefinedGeometryEnabled && stackIndex !== undefined;
	const isWhiteBrand = isNormallyWhiteBrand(agent.agentIcon);
	const centerStrokeColor =
		isRefinedGeometryEnabled && colorMode === 'light' && isWhiteBrand
			? token('color.background.accent.gray.subtler')
			: undefined;
	const presenceOutline = useMemo<PresenceHexOutline | undefined>(
		() =>
			isRefinedGeometryEnabled
				? {
						width: usesSharedFacepileOutline ? 0 : 2,
						svgHeight: size === 'small' ? 27 : 36,
						centerStrokeColor,
						centerStrokeWidth: centerStrokeColor ? 1 : undefined,
					}
				: undefined,
		[isRefinedGeometryEnabled, usesSharedFacepileOutline, size, centerStrokeColor],
	);
	const renderAgentAvatar = (
		avatarSize: AgentAvatarSize,
		presenceArtwork?: PresenceArtworkOptions,
	) =>
		agent.agentIcon ? (
			<AgentRowAvatar
				name={agent.name}
				agentIcon={agent.agentIcon}
				avatarSize={avatarSize}
				hasInlineSpacing={false}
				colorMode={presenceArtwork?.colorMode}
				borderColor={presenceArtwork?.borderColor}
				presenceTileFill={presenceArtwork?.presenceTileFill}
				presenceOutline={presenceArtwork?.presenceOutline}
				usePresenceArtwork={presenceArtwork ? true : undefined}
			/>
		) : (
			<AgentAvatar
				key={agent.id}
				imageUrl={agent.avatarUrl}
				agentId={agent.id}
				agentIdentityAccountId={agent.identity_account_id}
				agentNamedId={agent.external_config_reference ?? agent.named_id}
				size={avatarSize}
				showBorder={false}
				isForgeAgent={isForgeAgent(agent)}
				forgeAgentIconUrl={agent.icon}
			/>
		);

	if (!isAgentPresenceMoveEnabled) {
		return (
			<Box
				xcss={cx(showBorder && styles.legacyBorder)}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Restores the legacy runtime z-index used by the standalone agent avatar group.
				style={{ zIndex: stackIndex }}
			>
				{renderAgentAvatar('medium')}
			</Box>
		);
	}

	const avatarSize = size;
	let presenceArtwork: PresenceArtworkOptions | undefined;
	if (isRefinedGeometryEnabled) {
		const borderColor = usesSharedFacepileOutline ? 'transparent' : token('elevation.surface');
		const presenceTileFill =
			colorMode === 'dark' && isWhiteBrand
				? token('color.background.accent.gray.subtler')
				: undefined;
		presenceArtwork = { borderColor, colorMode, presenceTileFill, presenceOutline };
	}
	const shellBorderColor = usesSharedFacepileOutline
		? 'transparent'
		: isRefinedGeometryEnabled
			? colorMode === 'dark'
				? 'transparent'
				: token('elevation.surface')
			: undefined;
	const shellColors =
		showBorder && !agent.agentIcon ? getShellColors(agent, shellBorderColor) : undefined;
	const agentAvatar = renderAgentAvatar(avatarSize, presenceArtwork);

	return (
		<Box
			xcss={cx(
				showBorder && styles.root,
				showBorder && styles[avatarSize],
				isOverlapped && styles.overlapped,
			)}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Preserves the runtime stacking order used by the facepile.
			style={{ zIndex: stackIndex }}
			testId="agent-presence-avatar"
		>
			{showBorder && usesSharedFacepileOutline ? (
				<AgentPresenceOutline color={token('elevation.surface')} size={avatarSize} />
			) : null}
			{showBorder && shellColors ? (
				<>
					<AgentAvatarShell {...shellColors} size={avatarSize} />
					<Box as="span" xcss={styles.avatarContent}>
						{agentAvatar}
					</Box>
				</>
			) : usesSharedFacepileOutline ? (
				<Box as="span" xcss={styles.avatarContent}>
					{agentAvatar}
				</Box>
			) : (
				agentAvatar
			)}
		</Box>
	);
}
